import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useKeycloak} from '@react-keycloak/web';
import {useIntl} from 'react-intl';
import moment from 'moment';

import {
  Button,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  message,
  Row,
  Spin,
} from 'antd';

import {findTreatmentById, updateTreatment} from 'services/TreatmentService';
import {createTreatment} from 'services/PatientService';
import {handleError} from 'utils/MessageUtils';

const TreatmentEditor = (props) => {
  const {keycloak} = useKeycloak();
  const intl = useIntl();
  const [form] = Form.useForm();

  const headers = {
    Authorization: `Bearer ${keycloak?.token}`,
    'Accept-Language': intl.locale,
  };

  const {patientId, treatmentId, editing, setEditing, setReloading} = props;
  const [loading, setLoading] = useState(false);
  const [treatment, setTreatment] = useState({});

  useEffect(() => {
    if (editing && treatmentId?.length > 0) {
      setLoading(true);
      findTreatmentById(headers, treatmentId)
        .then((res) => {
          setTreatment(res.data);
          form.resetFields();
        })
        .catch((error) => handleError(message, error))
        .finally(() => setLoading(false));
    } else {
      form.resetFields();
    }
  }, [editing]);

  const onClose = () => {
    setTreatment(null);
    setEditing(false);
    setReloading(true);
  };

  const onReset = () => {
    form.resetFields();
  };

  const onSave = (values) => {
    const data = {
      ...values,
      period: {
        start: values.period?.start?.format('DD/MM/YYYY'),
        end: values.period?.end?.format('DD/MM/YYYY'),
      },
    };

    if (treatment?.id) {
      updateTreatment(headers, treatment.id, data)
        .then((res) => {
          setTreatment(res.data);
          setEditing(false);
          setReloading(true);
          message.success(intl.messages['common.updated']);
        })
        .catch((error) => handleError(message, error));
    } else {
      createTreatment(headers, patientId, data)
        .then((res) => {
          setTreatment(res.data);
          setEditing(false);
          setReloading(true);
          message.success(intl.messages['common.created']);
        })
        .catch((error) => handleError(message, error));
    }
  };

  return (
    <Drawer
      title={intl.messages['common.detail']}
      width={800}
      onClose={onClose}
      visible={editing}
      destroyOnClose={true}
      bodyStyle={{paddingBottom: 80}}>
      <Spin spinning={loading}>
        <Form
          layout='vertical'
          form={form}
          onFinish={onSave}
          initialValues={{
            ...treatment,
            period: treatment?.period
              ? {
                  start: treatment.period.start
                    ? moment(treatment.period.start, 'DD/MM/YYYY')
                    : null,
                  end: treatment.period.end
                    ? moment(treatment.period.end, 'DD/MM/YYYY')
                    : null,
                }
              : null,
          }}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name='name'
                label={intl.messages['treatment.name']}
                rules={[
                  {
                    required: true,
                    message: `${intl.messages['treatment.name']} ${intl.messages['validation.required']}`,
                  },
                ]}>
                <Input maxLength={100} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name={['period', 'start']}
                label={intl.messages['treatment.startTime']}
                rules={[
                  {
                    required: true,
                    message: `${intl.messages['treatment.startTime']} ${intl.messages['validation.required']}`,
                  },
                ]}>
                <DatePicker format='DD/MM/YYYY' style={{width: '100%'}} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={['period', 'end']}
                label={intl.messages['treatment.endTime']}>
                <DatePicker format='DD/MM/YYYY' style={{width: '100%'}} />
              </Form.Item>
            </Col>
          </Row>
          <Row glutter={16}>
            <Col span={24}>
              <Form.Item name='note' label={intl.messages['treatment.note']}>
                <Input.TextArea rows={5} maxLength={500} showCount />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={9} />
            <Form.Item shouldUpdate noStyle>
              <Col span={3}>
                <Button onClick={onReset} style={{width: '90px'}}>
                  {intl.messages['common.reset']}
                </Button>
              </Col>
              <Col span={3}>
                <Button
                  type='primary'
                  htmlType='submit'
                  style={{width: '90px'}}>
                  {intl.messages['common.save']}
                </Button>
              </Col>
            </Form.Item>
            <Col span={9} />
          </Row>
        </Form>
      </Spin>
    </Drawer>
  );
};

export default TreatmentEditor;

TreatmentEditor.propTypes = {
  patientId: PropTypes.string.isRequired,
  treatmentId: PropTypes.string,
  editing: PropTypes.bool.isRequired,
  setEditing: PropTypes.func.isRequired,
  setReloading: PropTypes.func.isRequired,
};
